import { useEffect, useLayoutEffect, useRef, type MutableRefObject } from 'react';

export interface EventListenerOptions {
  /** Listen only while true. Defaults to true. */
  enabled?: boolean;
  capture?: boolean;
}

function useLatest<T>(value: T): MutableRefObject<T> {
  const ref = useRef(value);
  useLayoutEffect(() => {
    ref.current = value;
  }, [value]);
  return ref;
}

function hasWindow(): boolean {
  return typeof window !== 'undefined';
}

/**
 * Listens for `type` on the window while `enabled`. The handler is read at
 * event time, so passing a fresh function each render never re-subscribes.
 */
export function useWindowEvent<K extends keyof WindowEventMap>(
  type: K,
  handler: (event: WindowEventMap[K]) => void,
  { enabled = true, capture = false }: EventListenerOptions = {},
): void {
  const latest = useLatest(handler);
  useEffect(() => {
    if (!enabled || !hasWindow()) return undefined;
    function listener(event: WindowEventMap[K]): void {
      latest.current(event);
    }
    window.addEventListener(type, listener, capture);
    return () => window.removeEventListener(type, listener, capture);
  }, [type, enabled, capture, latest]);
}

/** `useWindowEvent`'s twin for document-level events (keyboard shortcuts, outside clicks). */
export function useDocumentEvent<K extends keyof DocumentEventMap>(
  type: K,
  handler: (event: DocumentEventMap[K]) => void,
  { enabled = true, capture = false }: EventListenerOptions = {},
): void {
  const latest = useLatest(handler);
  useEffect(() => {
    if (!enabled || !hasWindow()) return undefined;
    function listener(event: DocumentEventMap[K]): void {
      latest.current(event);
    }
    document.addEventListener(type, listener, capture);
    return () => document.removeEventListener(type, listener, capture);
  }, [type, enabled, capture, latest]);
}
